import React from "react";
import { motion } from "framer-motion";

// The signature "Carpark-to-8,000 Curve"
// A small square (carpark + folding table) rises via a curved path
// into a dense cluster of dots representing 8,000+ people.
export const CarparkCurve = ({ compact = false, className = "" }) => {
  const dots = React.useMemo(() => {
    const arr = [];
    // Flatten and shift the cluster down so it sits clearly below the big
    // "8,000+" headline in the upper right.
    const cx = 470;
    const cy = 130;
    const rx = 90;
    const ry = 42;
    const count = 60;
    for (let i = 0; i < count; i++) {
      // deterministic pseudo-random
      const t = (i * 9301 + 49297) % 233280;
      const rand1 = t / 233280;
      const t2 = (i * 4523 + 71) % 233280;
      const rand2 = t2 / 233280;
      const angle = rand1 * Math.PI * 2;
      const radius = Math.sqrt(rand2);
      arr.push({
        x: cx + Math.cos(angle) * rx * radius,
        y: cy + Math.sin(angle) * ry * radius,
        r: 2 + rand1 * 2.5,
        delay: 1.4 + rand1 * 1.6,
      });
    }
    return arr;
  }, []);

  return (
    <svg
      viewBox="0 0 620 260"
      className={`w-full h-auto ${className}`}
      role="img"
      aria-label="Signature graphic: a small carpark square rising via a curve into a dense cluster of dots representing 8,000+ people"
      data-testid="carpark-curve"
    >
      {/* Baseline hairline */}
      <line
        x1="20"
        y1="220"
        x2="600"
        y2="220"
        stroke="#343437"
        strokeWidth="1"
      />

      {/* Folding table — the origin of the story, in a carpark */}
      <motion.g
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        {/* Supplies box on top (the food packs) */}
        <rect
          x="55"
          y="180"
          width="16"
          height="9"
          fill="none"
          stroke="#D8A936"
          strokeWidth="1.2"
        />
        <line x1="55" y1="184.5" x2="71" y2="184.5" stroke="#D8A936" strokeWidth="0.8" opacity="0.6" />
        {/* Tabletop slab */}
        <rect x="38" y="190" width="50" height="3.2" fill="#F3EFE6" />
        {/* Crossed folding legs (the signature X of a folding table) */}
        <line x1="44" y1="193.2" x2="82" y2="216" stroke="#F3EFE6" strokeWidth="1.4" strokeLinecap="round" />
        <line x1="82" y1="193.2" x2="44" y2="216" stroke="#F3EFE6" strokeWidth="1.4" strokeLinecap="round" />
        {/* Ground line under the legs */}
        <line x1="40" y1="216.5" x2="86" y2="216.5" stroke="#6E6E6B" strokeWidth="0.8" opacity="0.7" />
        <text
          x="63"
          y="238"
          textAnchor="middle"
          fill="#A9A9A6"
          fontFamily="Plus Jakarta Sans, sans-serif"
          fontSize="10"
          letterSpacing="0.16em"
        >
          400 · CARPARK
        </text>
      </motion.g>

      {/* The rising curve — starts from the tabletop, ends at the dot cluster centre */}
      <motion.path
        d="M 88 192 C 200 188, 280 175, 340 155 S 430 130, 470 130"
        fill="none"
        stroke="#D8A936"
        strokeWidth="1.8"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 2.2, delay: 0.5, ease: [0.16, 0.84, 0.24, 1] }}
      />

      {/* Dot cluster */}
      <g>
        {dots.map((d) => (
          <motion.circle
            key={`dot-${d.x.toFixed(2)}-${d.y.toFixed(2)}`}
            cx={d.x}
            cy={d.y}
            r={d.r}
            fill="#D8A936"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 0.9, scale: 1 }}
            transition={{ duration: 0.35, delay: d.delay }}
          />
        ))}
      </g>

      {/* Big 8,000+ headline — sits above the dot cluster, top-right corner */}
      <motion.g
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 2.4 }}
      >
        <text
          x="595"
          y="45"
          textAnchor="end"
          fill="#F3EFE6"
          fontFamily="Plus Jakarta Sans, sans-serif"
          fontWeight="800"
          fontSize="42"
          letterSpacing="-0.03em"
        >
          8,000+
        </text>
        <text
          x="595"
          y="66"
          textAnchor="end"
          fill="#A9A9A6"
          fontFamily="Plus Jakarta Sans, sans-serif"
          fontSize="11"
          letterSpacing="0.02em"
        >
          Individual students served weekly
        </text>
      </motion.g>

      {/* Right-side origin/endpoint label — mirrors "400 · CARPARK" under the folding table */}
      <motion.text
        x="470"
        y="238"
        textAnchor="middle"
        fill="#D8A936"
        fontFamily="Plus Jakarta Sans, sans-serif"
        fontSize="10"
        letterSpacing="0.16em"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 2.6 }}
      >
        400 → 8,000+
      </motion.text>
    </svg>
  );
};

// Compact monochrome logo mark for header/footer — miniature folding table
// rising into a dot cluster, mirroring the full CarparkCurve narrative.
export const CurveMark = ({ className = "" }) => (
  <svg viewBox="0 0 60 34" className={className} aria-hidden="true">
    {/* Folding table */}
    <rect x="2" y="24" width="14" height="1.4" fill="currentColor" />
    <line x1="4" y1="25.4" x2="14" y2="32" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" />
    <line x1="14" y1="25.4" x2="4" y2="32" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" />
    {/* Rising curve */}
    <path
      d="M 16 26 C 25 22, 32 16, 40 10"
      fill="none"
      stroke="#D8A936"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    {/* Dot cluster */}
    <circle cx="46" cy="8" r="1.6" fill="#D8A936" />
    <circle cx="50" cy="11" r="1.6" fill="#D8A936" />
    <circle cx="54" cy="7" r="1.6" fill="#D8A936" />
    <circle cx="52" cy="14" r="1.6" fill="#D8A936" />
    <circle cx="47" cy="14" r="1.6" fill="#D8A936" />
    <circle cx="43" cy="6" r="1.6" fill="#D8A936" />
  </svg>
);
